import React, { useState, useMemo } from 'react';
import { OfferCard } from './OfferCard';
import { OfferDetailsModal } from './OfferDetailsModal';
import { Button } from './ui/button';
import { offers, getFeaturedOffers, getOffersByCategory, sortOffers } from '../data/offers';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

export const OffersSection = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [sortBy, setSortBy] = useState('popular');
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const featuredOffers = getFeaturedOffers();

  const filteredAndSortedOffers = useMemo(() => {
    const filtered = getOffersByCategory(selectedCategory);
    return sortOffers(filtered, sortBy);
  }, [selectedCategory, sortBy]);

  const categories = ['All', 'Streaming', 'Music', 'Apple', 'Other'];

  const handleViewDetails = (offer) => {
    setSelectedOffer(offer);
    setIsModalOpen(true);
  };

  return (
    <section id="offers" className="pb-20 px-4">
      <div className="container mx-auto">
        {/* Featured Offers */}
        <div className="mb-20">
          <div className="text-center space-y-4 mb-12">
            <h2 className="text-4xl sm:text-5xl font-bold text-foreground">
              <span className="gradient-text">Featured</span> Offers
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Our most popular subscriptions with the biggest savings
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {featuredOffers.map((offer) => (
              <OfferCard
                key={offer.id}
                offer={offer}
                onViewDetails={handleViewDetails}
                featured={true}
              />
            ))}
          </div>
        </div>

        {/* All Offers with Filters */}
        <div>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6 mb-8">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-2">All Offers</h2>
              <p className="text-muted-foreground">Browse our complete collection</p>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-3 w-full sm:w-auto">
              {/* Category Filter */}
              <div className="flex gap-2 flex-wrap">
                {categories.map((category) => (
                  <Button
                    key={category}
                    variant={selectedCategory === category ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedCategory(category)}
                    className={selectedCategory === category ? 'bg-primary hover:bg-primary-glow' : ''}
                  >
                    {category}
                  </Button>
                ))}
              </div>

              {/* Sort Dropdown */}
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[180px] glass-card">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent className="glass-card">
                  <SelectItem value="popular">Popular</SelectItem>
                  <SelectItem value="discount">Highest Discount</SelectItem>
                  <SelectItem value="price">Lowest Price</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Offers Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredAndSortedOffers.map((offer) => (
              <OfferCard
                key={offer.id}
                offer={offer}
                onViewDetails={handleViewDetails}
              />
            ))}
          </div>

          {/* Transparency Note */}
          <div className="mt-12 text-center">
            <div className="inline-block glass-card px-6 py-4 rounded-lg">
              <p className="text-sm text-muted-foreground">
                <span className="font-semibold text-foreground">Transparency:</span> Prices and availability can vary by region. 
                Confirm in Instagram DM before purchase.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Offer Details Modal */}
      <OfferDetailsModal
        offer={selectedOffer}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </section>
  );
};
